/**
 * Canvas Operations Module
 *
 * This module contains utilities for canvas-related operations including:
 * - Aligning guidelines (snapping objects to each other)
 * - Centering guidelines (snapping objects to canvas center)
 * - Position calculations for object placement
 */

import { fabric } from "fabric";

/**
 * Initializes aligning guidelines for snapping objects to each other
 * Provides visual guides and distance markers when moving objects
 *
 * @param {fabric.Canvas} canvas - The Fabric.js canvas instance
 */
export const initAligningGuidelines = (canvas) => {
  var ctx = canvas.getSelectionContext(),
    // Offset of the guideline from the aligned object's edge.
    // A larger value will draw the guideline further away from the object.
    aligningLineOffset = 5,
    // Snapping sensitivity. A larger value makes snapping occur from a wider range.
    aligningLineMargin = 4,
    // Width of the alignment guideline in pixels.
    aligningLineWidth = 1,
    // Color of the alignment guideline
    aligningLineColor = "#FFC100",
    viewportTransform,
    zoom = 1;

  function drawVerticalLine(coords) {
    drawLine(
      coords.x + 0.5,
      coords.y1 > coords.y2 ? coords.y2 : coords.y1,
      coords.x + 0.5,
      coords.y2 > coords.y1 ? coords.y2 : coords.y1,
    );
  }

  function drawHorizontalLine(coords) {
    drawLine(
      coords.x1 > coords.x2 ? coords.x2 : coords.x1,
      coords.y + 0.5,
      coords.x2 > coords.x1 ? coords.x2 : coords.x1,
      coords.y + 0.5,
    );
  }

  function drawLine(x1, y1, x2, y2) {
    ctx.save();
    ctx.lineWidth = aligningLineWidth;
    ctx.strokeStyle = aligningLineColor;
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(x1 * zoom + viewportTransform[4], y1 * zoom + viewportTransform[5]);
    ctx.lineTo(x2 * zoom + viewportTransform[4], y2 * zoom + viewportTransform[5]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  function drawDistanceMarker(marker) {
    ctx.save();
    ctx.strokeStyle = "#00AEEF"; // blue lines
    ctx.fillStyle = "#00AEEF";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 2]);

    if (marker.type === "horizontal") {
      ctx.beginPath();
      ctx.moveTo(marker.x1 * zoom + viewportTransform[4], marker.y * zoom + viewportTransform[5]);
      ctx.lineTo(marker.x2 * zoom + viewportTransform[4], marker.y * zoom + viewportTransform[5]);
      ctx.stroke();

      let midX = (marker.x1 + marker.x2) / 2;
      ctx.font = "12px Arial";
      ctx.textAlign = "center";
      ctx.fillText(
        marker.distance.toFixed(0),
        midX * zoom + viewportTransform[4],
        (marker.y - 5) * zoom + viewportTransform[5],
      );
    }

    if (marker.type === "vertical") {
      ctx.beginPath();
      ctx.moveTo(marker.x * zoom + viewportTransform[4], marker.y1 * zoom + viewportTransform[5]);
      ctx.lineTo(marker.x * zoom + viewportTransform[4], marker.y2 * zoom + viewportTransform[5]);
      ctx.stroke();

      let midY = (marker.y1 + marker.y2) / 2;
      ctx.font = "12px Arial";
      ctx.textAlign = "left";
      ctx.fillText(
        marker.distance.toFixed(0),
        (marker.x + 5) * zoom + viewportTransform[4],
        midY * zoom + viewportTransform[5],
      );
    }

    ctx.restore();
  }

  function isInRange(value1, value2) {
    value1 = Math.round(value1);
    value2 = Math.round(value2);
    for (var i = value1 - aligningLineMargin, len = value1 + aligningLineMargin; i <= len; i++) {
      if (i === value2) {
        return true;
      }
    }
    return false;
  }

  var verticalLines = [],
    horizontalLines = [],
    distanceMarkers = [];

  canvas.on("mouse:down", function () {
    viewportTransform = canvas.viewportTransform;
    zoom = canvas.getZoom();
  });

  canvas.on("object:moving", function (e) {
    var activeObject = e.target;

    // Check if the object being moved is locked
    if (activeObject.lockMovementX && activeObject.lockMovementY) {
      e.e.stopPropagation();
      return;
    }

    // Check if this is an active selection with locked objects
    if (activeObject.type === "activeSelection") {
      const lockedObjects = activeObject.getObjects().filter((obj) => obj.lockMovementX && obj.lockMovementY);
      if (lockedObjects.length > 0) {
        e.e.stopPropagation();
        return;
      }
    }

    var canvasObjects = canvas.getObjects(),
      activeObjectCenter = activeObject.getCenterPoint(),
      activeObjectLeft = activeObjectCenter.x,
      activeObjectTop = activeObjectCenter.y,
      activeObjectBoundingRect = activeObject.getBoundingRect(),
      activeObjectHeight = activeObjectBoundingRect.height / viewportTransform[3],
      activeObjectWidth = activeObjectBoundingRect.width / viewportTransform[0],
      horizontalInTheRange = false,
      verticalInTheRange = false,
      transform = canvas._currentTransform;

    if (!transform) return;

    // It should be trivial to DRY this up by encapsulating (repeating) creation of x1, x2, y1, and y2 into functions,
    // but we're not doing it here for perf. reasons -- as this a function that's invoked on every mouse move

    for (var i = canvasObjects.length; i--; ) {
      if (canvasObjects[i] === activeObject) continue;

      var objectCenter = canvasObjects[i].getCenterPoint(),
        objectLeft = objectCenter.x,
        objectTop = objectCenter.y,
        objectBoundingRect = canvasObjects[i].getBoundingRect(),
        objectHeight = objectBoundingRect.height / viewportTransform[3],
        objectWidth = objectBoundingRect.width / viewportTransform[0];

      // ----------------
      // ✅ Distance Markers
      // ----------------
      // Horizontal gap (active right → object left)
      let distX = objectLeft - objectWidth / 2 - (activeObjectLeft + activeObjectWidth / 2);
      let overlapsVertically = !(
        activeObjectTop + activeObjectHeight / 2 < objectTop - objectHeight / 2 ||
        activeObjectTop - activeObjectHeight / 2 > objectTop + objectHeight / 2
      );
      if (distX > 0 && overlapsVertically) {
        distanceMarkers.push({
          type: "horizontal",
          distance: distX,
          x1: activeObjectLeft + activeObjectWidth / 2,
          x2: objectLeft - objectWidth / 2,
          y: Math.max(activeObjectTop - activeObjectHeight / 4, objectTop - objectHeight / 4),
        });
      }

      // Horizontal gap (object right → active left)
      let distX2 = activeObjectLeft - activeObjectWidth / 2 - (objectLeft + objectWidth / 2);
      if (distX2 > 0 && overlapsVertically) {
        distanceMarkers.push({
          type: "horizontal",
          distance: distX2,
          x1: objectLeft + objectWidth / 2,
          x2: activeObjectLeft - activeObjectWidth / 2,
          y: Math.max(activeObjectTop - activeObjectHeight / 4, objectTop - objectHeight / 4),
        });
      }

      // Vertical gap (active bottom → object top)
      let distY = objectTop - objectHeight / 2 - (activeObjectTop + activeObjectHeight / 2);
      let overlapsHorizontally = !(
        activeObjectLeft + activeObjectWidth / 2 < objectLeft - objectWidth / 2 ||
        activeObjectLeft - activeObjectWidth / 2 > objectLeft + objectWidth / 2
      );
      if (distY > 0 && overlapsHorizontally) {
        distanceMarkers.push({
          type: "vertical",
          distance: distY,
          y1: activeObjectTop + activeObjectHeight / 2,
          y2: objectTop - objectHeight / 2,
          x: Math.max(activeObjectLeft - activeObjectWidth / 4, objectLeft - objectWidth / 4),
        });
      }

      // Vertical gap (object bottom → active top)
      let distY2 = activeObjectTop - activeObjectHeight / 2 - (objectTop + objectHeight / 2);
      if (distY2 > 0 && overlapsHorizontally) {
        distanceMarkers.push({
          type: "vertical",
          distance: distY2,
          y1: objectTop + objectHeight / 2,
          y2: activeObjectTop - activeObjectHeight / 2,
          x: Math.max(activeObjectLeft - activeObjectWidth / 4, objectLeft - objectWidth / 4),
        });
      }

      // snaps if the right side of the active object touches the left side of the object
      if (isInRange(activeObjectLeft + activeObjectWidth / 2, objectLeft - objectWidth / 2)) {
        verticalInTheRange = true;
        verticalLines.push({
          x: objectLeft - objectWidth / 2,
          y1:
            objectTop < activeObjectTop
              ? objectTop - objectHeight / 2 - aligningLineOffset
              : objectTop + objectHeight / 2 + aligningLineOffset,
          y2:
            activeObjectTop > objectTop
              ? activeObjectTop + activeObjectHeight / 2 + aligningLineOffset
              : activeObjectTop - activeObjectHeight / 2 - aligningLineOffset,
        });

        activeObject.setPositionByOrigin(
          new fabric.Point(objectLeft - objectWidth / 2 - activeObjectWidth / 2, activeObjectTop),
          "center",
          "center",
        );
      }

      // snaps if the left side of the active object touches the right side of the object
      if (isInRange(activeObjectLeft - activeObjectWidth / 2, objectLeft + objectWidth / 2)) {
        verticalInTheRange = true;
        verticalLines.push({
          x: objectLeft + objectWidth / 2,
          y1:
            objectTop < activeObjectTop
              ? objectTop - objectHeight / 2 - aligningLineOffset
              : objectTop + objectHeight / 2 + aligningLineOffset,
          y2:
            activeObjectTop > objectTop
              ? activeObjectTop + activeObjectHeight / 2 + aligningLineOffset
              : activeObjectTop - activeObjectHeight / 2 - aligningLineOffset,
        });

        activeObject.setPositionByOrigin(
          new fabric.Point(objectLeft + objectWidth / 2 + activeObjectWidth / 2, activeObjectTop),
          "center",
          "center",
        );
      }

      // snaps if the bottom of the object touches the top of the active object
      if (isInRange(objectTop + objectHeight / 2, activeObjectTop - activeObjectHeight / 2)) {
        horizontalInTheRange = true;
        horizontalLines.push({
          y: objectTop + objectHeight / 2,
          x1:
            objectLeft < activeObjectLeft
              ? objectLeft - objectWidth / 2 - aligningLineOffset
              : objectLeft + objectWidth / 2 + aligningLineOffset,
          x2:
            activeObjectLeft > objectLeft
              ? activeObjectLeft + activeObjectWidth / 2 + aligningLineOffset
              : activeObjectLeft - activeObjectWidth / 2 - aligningLineOffset,
        });

        activeObject.setPositionByOrigin(
          new fabric.Point(activeObjectLeft, objectTop + objectHeight / 2 + activeObjectHeight / 2),
          "center",
          "center",
        );
      }

      // snaps if the top of the object touches the bottom of the active object
      if (isInRange(objectTop - objectHeight / 2, activeObjectTop + activeObjectHeight / 2)) {
        horizontalInTheRange = true;
        horizontalLines.push({
          y: objectTop - objectHeight / 2,
          x1:
            objectLeft < activeObjectLeft
              ? objectLeft - objectWidth / 2 - aligningLineOffset
              : objectLeft + objectWidth / 2 + aligningLineOffset,
          x2:
            activeObjectLeft > objectLeft
              ? activeObjectLeft + activeObjectWidth / 2 + aligningLineOffset
              : activeObjectLeft - activeObjectWidth / 2 - aligningLineOffset,
        });

        activeObject.setPositionByOrigin(
          new fabric.Point(activeObjectLeft, objectTop - objectHeight / 2 - activeObjectHeight / 2),
          "center",
          "center",
        );
      }

      // snap by the horizontal center line
      if (isInRange(objectLeft, activeObjectLeft)) {
        verticalInTheRange = true;
        verticalLines.push({
          x: objectLeft,
          y1:
            objectTop < activeObjectTop
              ? objectTop - objectHeight / 2 - aligningLineOffset
              : objectTop + objectHeight / 2 + aligningLineOffset,
          y2:
            activeObjectTop > objectTop
              ? activeObjectTop + activeObjectHeight / 2 + aligningLineOffset
              : activeObjectTop - activeObjectHeight / 2 - aligningLineOffset,
        });
        activeObject.setPositionByOrigin(new fabric.Point(objectLeft, activeObjectTop), "center", "center");
      }

      // snap by the left edge
      if (isInRange(objectLeft - objectWidth / 2, activeObjectLeft - activeObjectWidth / 2)) {
        verticalInTheRange = true;
        verticalLines.push({
          x: objectLeft - objectWidth / 2,
          y1:
            objectTop < activeObjectTop
              ? objectTop - objectHeight / 2 - aligningLineOffset
              : objectTop + objectHeight / 2 + aligningLineOffset,
          y2:
            activeObjectTop > objectTop
              ? activeObjectTop + activeObjectHeight / 2 + aligningLineOffset
              : activeObjectTop - activeObjectHeight / 2 - aligningLineOffset,
        });
        activeObject.setPositionByOrigin(
          new fabric.Point(objectLeft - objectWidth / 2 + activeObjectWidth / 2, activeObjectTop),
          "center",
          "center",
        );
      }

      // snap by the right edge
      if (isInRange(objectLeft + objectWidth / 2, activeObjectLeft + activeObjectWidth / 2)) {
        verticalInTheRange = true;
        verticalLines.push({
          x: objectLeft + objectWidth / 2,
          y1:
            objectTop < activeObjectTop
              ? objectTop - objectHeight / 2 - aligningLineOffset
              : objectTop + objectHeight / 2 + aligningLineOffset,
          y2:
            activeObjectTop > objectTop
              ? activeObjectTop + activeObjectHeight / 2 + aligningLineOffset
              : activeObjectTop - activeObjectHeight / 2 - aligningLineOffset,
        });
        activeObject.setPositionByOrigin(
          new fabric.Point(objectLeft + objectWidth / 2 - activeObjectWidth / 2, activeObjectTop),
          "center",
          "center",
        );
      }

      // snap by the vertical center line
      if (isInRange(objectTop, activeObjectTop)) {
        horizontalInTheRange = true;
        horizontalLines.push({
          y: objectTop,
          x1:
            objectLeft < activeObjectLeft
              ? objectLeft - objectWidth / 2 - aligningLineOffset
              : objectLeft + objectWidth / 2 + aligningLineOffset,
          x2:
            activeObjectLeft > objectLeft
              ? activeObjectLeft + activeObjectWidth / 2 + aligningLineOffset
              : activeObjectLeft - activeObjectWidth / 2 - aligningLineOffset,
        });
        activeObject.setPositionByOrigin(new fabric.Point(activeObjectLeft, objectTop), "center", "center");
      }

      // snap by the top edge
      if (isInRange(objectTop - objectHeight / 2, activeObjectTop - activeObjectHeight / 2)) {
        horizontalInTheRange = true;
        horizontalLines.push({
          y: objectTop - objectHeight / 2,
          x1:
            objectLeft < activeObjectLeft
              ? objectLeft - objectWidth / 2 - aligningLineOffset
              : objectLeft + objectWidth / 2 + aligningLineOffset,
          x2:
            activeObjectLeft > objectLeft
              ? activeObjectLeft + activeObjectWidth / 2 + aligningLineOffset
              : activeObjectLeft - activeObjectWidth / 2 - aligningLineOffset,
        });
        activeObject.setPositionByOrigin(
          new fabric.Point(activeObjectLeft, objectTop - objectHeight / 2 + activeObjectHeight / 2),
          "center",
          "center",
        );
      }

      // snap by the bottom edge
      if (isInRange(objectTop + objectHeight / 2, activeObjectTop + activeObjectHeight / 2)) {
        horizontalInTheRange = true;
        horizontalLines.push({
          y: objectTop + objectHeight / 2,
          x1:
            objectLeft < activeObjectLeft
              ? objectLeft - objectWidth / 2 - aligningLineOffset
              : objectLeft + objectWidth / 2 + aligningLineOffset,
          x2:
            activeObjectLeft > objectLeft
              ? activeObjectLeft + activeObjectWidth / 2 + aligningLineOffset
              : activeObjectLeft - activeObjectWidth / 2 - aligningLineOffset,
        });
        activeObject.setPositionByOrigin(
          new fabric.Point(activeObjectLeft, objectTop + objectHeight / 2 - activeObjectHeight / 2),
          "center",
          "center",
        );
      }
    }

    if (!horizontalInTheRange) {
      horizontalLines.length = 0;
    }

    if (!verticalInTheRange) {
      verticalLines.length = 0;
    }
  });

  canvas.on("before:render", function () {
    if (canvas.contextTop) {
      canvas.clearContext(canvas.contextTop);
    }
  });

  canvas.on("after:render", function () {
    for (var i = verticalLines.length; i--; ) {
      drawVerticalLine(verticalLines[i]);
    }
    for (var j = horizontalLines.length; j--; ) {
      drawHorizontalLine(horizontalLines[j]);
    }

    for (var k = distanceMarkers.length; k--; ) {
      drawDistanceMarker(distanceMarkers[k]);
    }

    verticalLines.length = horizontalLines.length = distanceMarkers.length = 0;
  });

  canvas.on("mouse:up", function () {
    verticalLines.length = horizontalLines.length = distanceMarkers.length = 0;
    canvas.renderAll();
  });
};

/**
 * Initializes centering guidelines for snapping objects to canvas center
 * Shows visual guides when an object is near the horizontal or vertical center of the workspace
 *
 * @param {fabric.Canvas} canvas - The Fabric.js canvas instance
 * @param {fabric.Object} workspace - The workspace object that defines the canvas bounds
 */
export const initCenteringGuidelines = (canvas, workspace) => {
  if (!workspace) return;

  // Defines the sensitivity for snapping to the center.
  // A larger value means the object will snap from a greater distance.
  var centerLineMargin = 4,
    // Sets the color of the centering guideline
    centerLineColor = "#FFC100",
    // Sets the width (thickness) of the centering guideline in pixels
    centerLineWidth = 1,
    ctx = canvas.getSelectionContext(),
    viewportTransform,
    zoom = 1;

  /**
   * Draws a vertical line on the canvas at the specified x-coordinate
   */
  function drawVerticalLine(x) {
    drawLine(
      x + 0.5,
      workspace.top - workspace.top * zoom + viewportTransform[5],
      x + 0.5,
      workspace.top +
        workspace.height +
        (canvas.height - workspace.top - workspace.height) * zoom +
        viewportTransform[5],
    );
  }

  /**
   * Draws a horizontal line on the canvas at the specified y-coordinate
   */
  function drawHorizontalLine(y) {
    drawLine(
      workspace.left - workspace.left * zoom + viewportTransform[4],
      y + 0.5,
      workspace.left +
        workspace.width +
        (canvas.width - workspace.left - workspace.width) * zoom +
        viewportTransform[4],
      y + 0.5,
    );
  }

  /**
   * Draws a line from (x1, y1) to (x2, y2) with predefined styling
   */
  function drawLine(x1, y1, x2, y2) {
    ctx.save();
    ctx.lineWidth = centerLineWidth;
    ctx.strokeStyle = centerLineColor;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  /**
   * Checks if two values are within the center line margin range
   */
  function isInRange(value1, value2) {
    value1 = Math.round(value1);
    value2 = Math.round(value2);
    for (var i = value1 - centerLineMargin, len = value1 + centerLineMargin; i <= len; i++) {
      if (i === value2) {
        return true;
      }
    }
    return false;
  }

  var showVerticalCenterLine = false,
    showHorizontalCenterLine = false;

  canvas.on("mouse:down", function () {
    viewportTransform = canvas.viewportTransform;
    zoom = canvas.getZoom();
  });

  /**
   * Event handler for when an object is being moved
   * Checks if the object is near the center and snaps it if within range
   */
  canvas.on("object:moving", function (e) {
    var activeObject = e.target,
      canvasWidth = workspace.width,
      canvasHeight = workspace.height,
      canvasCenter = {
        x: workspace.left + canvasWidth / 2,
        y: workspace.top + canvasHeight / 2,
      },
      activeObjectCenter = activeObject.getCenterPoint(),
      activeObjectLeft = activeObjectCenter.x,
      activeObjectTop = activeObjectCenter.y,
      transform = canvas._currentTransform;

    if (!transform) return;

    // Check vertical center alignment
    if (isInRange(activeObjectLeft, canvasCenter.x)) {
      showVerticalCenterLine = true;
      activeObject.setPositionByOrigin(new fabric.Point(canvasCenter.x, activeObjectTop), "center", "center");
    } else {
      showVerticalCenterLine = false;
    }

    // Check horizontal center alignment
    if (isInRange(activeObjectTop, canvasCenter.y)) {
      showHorizontalCenterLine = true;
      activeObject.setPositionByOrigin(new fabric.Point(activeObjectLeft, canvasCenter.y), "center", "center");
    } else {
      showHorizontalCenterLine = false;
    }
  });

  canvas.on("before:render", function () {
    if (canvas.contextTop) {
      canvas.clearContext(canvas.contextTop);
    }
  });

  canvas.on("after:render", function () {
    if (showVerticalCenterLine) {
      var verticalCanvasCenter = workspace.left + workspace.width / 2;
      drawVerticalLine(verticalCanvasCenter * zoom + viewportTransform[4]);
    }
    if (showHorizontalCenterLine) {
      var horizontalCanvasCenter = workspace.top + workspace.height / 2;
      drawHorizontalLine(horizontalCanvasCenter * zoom + viewportTransform[5]);
    }
  });

  canvas.on("mouse:up", function () {
    // Clear the guidelines when the mouse is released
    showVerticalCenterLine = showHorizontalCenterLine = false;
    canvas.renderAll();
  });
};

/**
 * Calculates the left and top coordinates for positioning an object
 * based on the specified position value (e.g., "top_left", "center_center")
 *
 * @param {string} positionValue - The position identifier (e.g., "top_left", "center_center", "bottom_right")
 * @param {number} wsLeft - The left coordinate of the workspace
 * @param {number} wsTop - The top coordinate of the workspace
 * @param {number} wsWidth - The width of the workspace
 * @param {number} wsHeight - The height of the workspace
 * @param {number} objectWidth - The width of the object to position
 * @param {number} objectHeight - The height of the object to position
 * @returns {{ left: number|undefined, top: number|undefined }} The calculated coordinates
 */
export const calculateObjectPositionCoordinates = (
  positionValue,
  wsLeft,
  wsTop,
  wsWidth,
  wsHeight,
  objectWidth,
  objectHeight,
) => {
  let newLeft;
  let newTop;

  switch (positionValue) {
    case "top_left":
      newLeft = wsLeft;
      newTop = wsTop;
      break;
    case "top_center":
      newLeft = wsLeft + wsWidth / 2 - objectWidth / 2;
      newTop = wsTop;
      break;
    case "top_right":
      newLeft = wsLeft + wsWidth - objectWidth;
      newTop = wsTop;
      break;
    case "center_left":
      newLeft = wsLeft;
      newTop = wsTop + wsHeight / 2 - objectHeight / 2;
      break;
    case "center_center":
      newLeft = wsLeft + wsWidth / 2 - objectWidth / 2;
      newTop = wsTop + wsHeight / 2 - objectHeight / 2;
      break;
    case "center_right":
      newLeft = wsLeft + wsWidth - objectWidth;
      newTop = wsTop + wsHeight / 2 - objectHeight / 2;
      break;
    case "bottom_left":
      newLeft = wsLeft;
      newTop = wsTop + wsHeight - objectHeight;
      break;
    case "bottom_center":
      newLeft = wsLeft + wsWidth / 2 - objectWidth / 2;
      newTop = wsTop + wsHeight - objectHeight;
      break;
    case "bottom_right":
      newLeft = wsLeft + wsWidth - objectWidth;
      newTop = wsTop + wsHeight - objectHeight;
      break;
    default:
      return { left: undefined, top: undefined };
  }
  return { left: newLeft, top: newTop };
};
